import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Sun, Moon, Clock } from 'lucide-react';

function DrawCountdown() {
  const [schedule, setSchedule] = useState(null);
  const [countdown, setCountdown] = useState({ midday: null, evening: null });

  useEffect(() => {
    fetchSchedule();
    const scheduleInterval = setInterval(fetchSchedule, 60000);
    return () => clearInterval(scheduleInterval);
  }, []);

  useEffect(() => {
    if (!schedule) return;
    const tick = setInterval(() => {
      const now = new Date();
      setCountdown({
        midday: getTimeRemaining(schedule.next_midday, now),
        evening: getTimeRemaining(schedule.next_evening, now),
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [schedule]);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/draws/schedule`);
      setSchedule(response.data);
    } catch (e) {
      // Non-critical
    }
  };

  const getTimeRemaining = (targetIso, now) => {
    const target = new Date(targetIso);
    const diff = target - now;
    if (diff <= 0) return null;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { hours, minutes, seconds, total: diff };
  };

  const formatTime = (time) => {
    if (!time) return "Drawing...";
    const { hours, minutes, seconds } = time;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const isUrgent = (time) => time && time.total < 300000; // < 5 minutes

  if (!schedule) return null;

  // Determine which draw is next
  const nextDraw = countdown.midday && countdown.evening
    ? (countdown.midday.total < countdown.evening.total ? 'midday' : 'evening')
    : countdown.midday ? 'midday' : 'evening';

  const nextTime = nextDraw === 'midday' ? countdown.midday : countdown.evening;
  const urgent = isUrgent(nextTime);

  return (
    <div data-testid="draw-countdown" className="px-4 pb-2">
      <div className={`flex items-center justify-between rounded-xl px-3.5 py-2.5 border ${
        urgent
          ? 'bg-red-500/5 border-red-500/25 animate-pulse'
          : 'bg-slate-800/40 border-slate-700/40'
      }`}>
        <div className="flex items-center gap-3">
          {/* Next draw indicator */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            nextDraw === 'midday' ? 'bg-amber-500/15' : 'bg-indigo-500/15'
          }`}>
            {nextDraw === 'midday'
              ? <Sun className="w-4 h-4 text-amber-400" />
              : <Moon className="w-4 h-4 text-indigo-400" />}
          </div>
          <div>
            <p className="text-[11px] text-slate-500">Next Draw</p>
            <p className="text-xs font-semibold text-white capitalize">{nextDraw} Draw</p>
          </div>
        </div>

        {/* Countdown */}
        <div className="text-right">
          <div className={`text-base font-bold font-mono tabular-nums ${
            urgent ? 'text-red-400' : 'text-orange-300'
          }`}>
            {formatTime(nextTime)}
          </div>
          <p className="text-[9px] text-slate-600">CT timezone</p>
        </div>
      </div>

      {/* Both draws mini row */}
      <div className="flex gap-2 mt-1.5">
        <div className="flex-1 flex items-center justify-between bg-slate-800/20 rounded-lg px-2.5 py-1.5">
          <div className="flex items-center gap-1.5">
            <Sun className="w-3 h-3 text-amber-400/60" />
            <span className="text-[10px] text-slate-500">Midday</span>
          </div>
          <span className={`text-[10px] font-mono font-semibold ${countdown.midday ? 'text-slate-300' : 'text-emerald-400'}`}>
            {countdown.midday ? formatTime(countdown.midday) : 'Live!'}
          </span>
        </div>
        <div className="flex-1 flex items-center justify-between bg-slate-800/20 rounded-lg px-2.5 py-1.5">
          <div className="flex items-center gap-1.5">
            <Moon className="w-3 h-3 text-indigo-400/60" />
            <span className="text-[10px] text-slate-500">Evening</span>
          </div>
          <span className={`text-[10px] font-mono font-semibold ${countdown.evening ? 'text-slate-300' : 'text-emerald-400'}`}>
            {countdown.evening ? formatTime(countdown.evening) : 'Live!'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default DrawCountdown;
